import { getMockProjects } from '../consts/mockProjects';
import { MarketplaceFilters, MarketplaceProject, SortOption } from '../types';

/**
 * Simulate API delay
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export interface FetchMarketplaceProjectsParams extends Partial<MarketplaceFilters> {
    page?: number;
    limit?: number;
}

export interface MarketplaceProjectsResult {
    projects: MarketplaceProject[];
    total: number;
    page: number;
    limit: number;
}

/**
 * Fetch marketplace projects with filters and pagination
 * TODO: Replace with real API call when backend is ready
 */
export const fetchMarketplaceProjects = async (
    params?: FetchMarketplaceProjectsParams
): Promise<MarketplaceProjectsResult> => {
    // Simulate network delay
    await delay(300);

    const { page = 0, limit = 9, ...filters } = params || {};

    let projects = [...getMockProjects()];

    // Apply category filter
    if (filters?.category && filters.category !== 'all') {
        projects = projects.filter(project => project.category === filters.category);
    }

    // Apply search filter
    if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        projects = projects.filter(
            project =>
                project.title.toLowerCase().includes(query) ||
                project.description.toLowerCase().includes(query) ||
                project.tags.some(tag => tag.toLowerCase().includes(query))
        );
    }

    // Apply sorting
    if (filters?.sortBy) {
        projects = sortProjects(projects, filters.sortBy);
    }

    const total = projects.length;
    const startIndex = page * limit;
    const endIndex = startIndex + limit;
    const paginatedProjects = projects.slice(startIndex, endIndex);

    return {
        projects: paginatedProjects,
        total,
        page,
        limit,
    };
};

/**
 * Fetch a single marketplace project by ID
 */
export const fetchMarketplaceProject = async (id: string): Promise<MarketplaceProject | null> => {
    await delay(200);
    return getMockProjects().find(project => project.id === id) || null;
};

/**
 * Sort projects based on selected option
 */
const sortProjects = (projects: MarketplaceProject[], sortBy: SortOption): MarketplaceProject[] => {
    const sorted = [...projects];

    switch (sortBy) {
        case 'recent':
            return sorted.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        case 'popular':
            return sorted.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        case 'name':
            return sorted.sort((a, b) => a.title.localeCompare(b.title));
        default:
            return sorted;
    }
};
